import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { EditorOrphanWarning } from '@/lib/analyst-reading';
import {
  orphanWarningSentence,
  pointerSectionSentence,
  readingWarningTitle,
  saveSectionSentence,
  sectionIncompleteLead,
} from '@/lib/analyst-reading';
import type { AnalystSectionGaps, UsageLine } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';

/**
 * Le côté protocole de la section Analyste : l'encart jaune des deux boîtes qui
 * NOMME les expériences à bloc Noyau qu'un geste empêcherait de démarrer
 * (analyste.md §3.7, K16 ; noyau.md N37), et - depuis #158 - les pastilles
 * orphelines de la version (N52 ; maquette `LectureVariablesOrpheline`).
 *
 * ## A warning, never a refusal
 *
 * « Un objet ne refuse jamais à cause de ce qui le cite » (P9, K16): the version
 * saves, the pointer moves, and the button stays armed (N37). What the box owes the
 * operator is the EFFECT, before the click - the régime of the texts of the noyaux
 * (§6.5) - so the encart names each experience whose sessions would no longer start,
 * and says nothing when there is none: a protocol with an incomplete section that no
 * noyau experience plays breaks nothing.
 *
 * ## Two sentences, one per gesture
 *
 *   - `save` - « Enregistrer une nouvelle version »: the section of the version is
 *     incomplete, said ONCE and with what it lacks (the words of the encart of the
 *     editor), then each experience « joue la séquence du noyau avec ce protocole : ses
 *     sessions ne démarreront plus ». The lead opens the first line, so one experience
 *     reads as one paragraph.
 *   - `pointer` - « Passer en production », a rollback, « Reculer développement »: set
 *     under the row of the experience that will play the version, each « joue la séquence
 *     du noyau : avec une section Analyste incomplète, ses sessions ne démarreront
 *     plus ».
 *
 * ## The orphan chips, in the SAME encart (#158, #196)
 *
 * A reading or a direction that cites an objective the version no longer carries is
 * warned the same way, whatever the gesture and whoever plays the protocol (V2, N52):
 * « La lecture de la passe 2 cite un objectif qui n'existe plus dans cette version :
 * l'analyste lira « (objectif retiré) » à sa place. », « La direction de l'étape
 * « Émergence » cite un objectif qui n'existe plus dans cette version. » ONE yellow
 * encart for everything the box warns about, the experiences first - the section's own
 * fact, which excludes its orphan - then each orphan in the order of
 * `sectionOrphanWarnings`, and the title counts them all: « 2 avertissements, jamais un
 * refus. » Since #196 each READING warns by its pass, and the reading of before #196,
 * which nothing serves, warns nobody.
 *
 * WHICH experiences and WHICH orphans are the caller's to decide - on the tags
 * the gesture moves, on the content the gesture writes -: this component only
 * says them.
 */

/** The `data-kind` each orphan line carries: a reading of the section, a step's directions, the consigne of the rangement. */
const ORPHAN_LINE_KIND: Readonly<Record<EditorOrphanWarning['kind'], string>> = {
  'analyst-reading-orphan': 'reading',
  'direction-variable-orphan': 'direction',
  'rangement-variable-orphan': 'rangement',
};

/** A key per orphan line: a reading by its pass, a step's directions by the step, the consigne once. */
function orphanKey(orphan: EditorOrphanWarning): string {
  switch (orphan.kind) {
    case 'analyst-reading-orphan':
      return `reading:${orphan.pass}`;
    case 'direction-variable-orphan':
      return `direction:${orphan.stepId}`;
    case 'rangement-variable-orphan':
      return orphan.kind;
  }
}

export interface ProtocolReadingWarningProps {
  /** The experiences the gesture would stop - empty names none. */
  readonly experiences: readonly UsageLine[];
  /** What the section of the version lacks, for the lead of a save - null when it is complete. */
  readonly gaps: AnalystSectionGaps | null;
  /** The orphan chips of the content the gesture writes or moves onto (#158) - empty says none. */
  readonly orphans: readonly EditorOrphanWarning[];
  readonly gesture: 'save' | 'pointer';
}

export function ProtocolReadingWarning({
  experiences,
  gaps,
  orphans,
  gesture,
}: ProtocolReadingWarningProps) {
  if (experiences.length === 0 && orphans.length === 0) return null;
  const sentence = gesture === 'save' ? saveSectionSentence : pointerSectionSentence;

  return (
    <div data-testid={PROTOCOL_TEST_IDS.readingWarning}>
      <Alert tone="warning" title={readingWarningTitle(experiences.length + orphans.length)}>
        <ul className="mt-1.5 space-y-1">
          {experiences.map((experience, index) => (
            <li
              key={experience.id}
              data-testid={PROTOCOL_TEST_IDS.readingWarningLine}
              data-experience-id={experience.id}
            >
              {gesture === 'save' && index === 0 && gaps !== null
                ? `${sectionIncompleteLead(gaps)} `
                : ''}
              {sentence(experience.name)}
            </li>
          ))}
          {orphans.map((orphan) => (
            <li
              key={orphanKey(orphan)}
              data-testid={PROTOCOL_TEST_IDS.readingOrphanLine}
              data-kind={ORPHAN_LINE_KIND[orphan.kind]}
              {...(orphan.kind === 'direction-variable-orphan'
                ? { 'data-step-id': orphan.stepId }
                : {})}
              {...(orphan.kind === 'analyst-reading-orphan' ? { 'data-pass': orphan.pass } : {})}
            >
              {orphanWarningSentence(orphan)}
            </li>
          ))}
        </ul>
      </Alert>
    </div>
  );
}
